import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, input, viewChild } from '@angular/core';
import {
  UPLOAD_PROGRESS_SOURCE,
  UPLOAD_SUPPORT_CONTACT,
  UploadProgressBarComponent,
  type UploadFailureEntry,
  type UploadProgressSignal,
  type UploadProgressSource,
} from '@lite-workspace/uploader-core';
import {
  PhotoUploadCoordinatorService,
  toUploadProgressSignal,
} from '../../features/projects/domain/application/photo-upload-coordinator.service';
import { TelegramContactService } from '../telegram-contact';

/**
 * Fotoclass's mounting point for the platform's ONE upload bar: it feeds the shared bar this project's
 * queue through `UPLOAD_PROGRESS_SOURCE` and owns nothing else — track, counter, inline cancel all
 * live in the shared layer.
 *
 * A failed run folds the bar away; the shared bar's sticky toast names the lost files and opens their window.
 */
@Component({
  selector: 'app-upload-progress-line',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UploadProgressBarComponent],
  providers: [
    { provide: UPLOAD_PROGRESS_SOURCE, useExisting: forwardRef(() => UploadProgressLineComponent) },
    // Fotoclass users do not reach a bare t.me link; support opens the app's channel choice at the pressed control.
    {
      provide: UPLOAD_SUPPORT_CONTACT,
      useFactory: (): (() => void) => {
        const telegram = inject(TelegramContactService);
        return () => telegram.open('support');
      },
    },
  ],
  host: { class: 'block', '[class.hidden]': '!visible()' },
  template: `<upload-progress-bar variant="inline" [showPhaseWord]="showPhaseWord()" />`,
})
export class UploadProgressLineComponent implements UploadProgressSource {
  private readonly coordinator = inject(PhotoUploadCoordinatorService);

  readonly projectId = input<string | null>(null);
  /** Off = the counter alone, flush against the cancel cross. */
  readonly showPhaseWord = input(true);

  private readonly bar = viewChild(UploadProgressBarComponent);

  private readonly progress = computed(() => this.coordinator.progressFor(this.projectId())());

  /** A host that reserves space for the bar (the editor's top strip) expands and collapses with it. */
  readonly visible = computed(() => this.bar()?.shown() ?? false);

  readonly snapshot = computed<UploadProgressSignal>(() => toUploadProgressSignal(this.progress()));

  readonly bytesSent = computed(() => this.progress().bytesSent);
  readonly bytesTotal = computed(() => this.progress().bytesTotal);
  readonly previewsReady = computed(() => this.progress().loaded);
  /** Previews are owed only by files the server took — the failed ones never get one. */
  readonly previewsTotal = computed(() => {
    const upload = this.progress();
    return Math.max(0, upload.total - upload.failed.length);
  });

  readonly stalled = computed(() => this.progress().stalled);
  /** Names what «Повторить» takes: this run's failures and those an earlier run left behind. */
  readonly failures = computed<readonly UploadFailureEntry[]>(() =>
    [...this.progress().failed, ...this.progress().earlierFailed].map(job => ({
      name: job.name,
      reason: job.error ?? 'Не удалось загрузить',
      retryable: job.retryable,
    })),
  );

  cancel(): void {
    this.coordinator.cancel(this.projectId());
  }

  retryAll(): void {
    this.coordinator.retryFailed(this.projectId());
  }
}
